import React, { useEffect, useState } from 'react';
import { Users, Plus, Trash2, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DataRoomSyndicate = ({ token }) => {
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchMembers = () => {
    fetch(`${API_URL}/api/dataroom/syndicate`, { headers: { 'X-Dataroom-Token': token } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) { setMembers(d.members || []); setTotal(d.total_committed || 0); setLoaded(true); } })
      .catch(() => {});
  };
  useEffect(() => { if (token) fetchMembers(); }, [token]);

  if (!loaded) return null;

  const addMember = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await fetch(`${API_URL}/api/dataroom/syndicate/member`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Dataroom-Token': token },
      body: JSON.stringify({ name: form.name, email: form.email, amount: Number(form.amount) || 0 }),
    }).catch(() => {});
    setForm({ name: '', email: '', amount: '' });
    setSaving(false);
    fetchMembers();
  };

  const removeMember = async (id) => {
    await fetch(`${API_URL}/api/dataroom/syndicate/member/${id}`, {
      method: 'DELETE', headers: { 'X-Dataroom-Token': token },
    }).catch(() => {});
    fetchMembers();
  };

  return (
    <div id="sec-syndicate" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-syndicate">
      <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2">
        <Users size={14} className="text-[#7B4FD8]" /> Your Syndicate
      </h3>
      <p className="text-[11px] text-gray-500 mb-4">
        As Lead Investor you can privately track the members investing through you. HiBFF records one shareholder of record — you. Member details below are visible only in this room and to the founder.
      </p>

      {/* Attorney disclaimer — onus on the Lead Investor */}
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-5 flex gap-3" data-testid="dr-syndicate-disclaimer">
        <ShieldAlert size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-800 leading-relaxed">
          <strong>Important — please read before adding members.</strong> If you intend to invest on behalf of others (a club, syndicate or pooled arrangement), you must consult your own attorney or solicitor <strong>before committing funds to HiBFF</strong>. Rules on pooled and collective investment vary by country. As Lead Investor, you are solely responsible for your arrangements with your members and for your own legal and regulatory compliance; HiBFF's relationship — and shareholder record — is with you alone.
        </p>
      </div>

      {members.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Members ({members.length})</p>
            <p className="text-[11px] font-black text-[#5B32C8]">Total tracked: ${Number(total).toLocaleString()}</p>
          </div>
          <div className="space-y-1.5">
            {members.map(m => (
              <div key={m.member_id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2" data-testid={`dr-syndicate-member-${m.member_id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-gray-800 truncate">{m.name}</p>
                  {m.email && <p className="text-[10px] text-gray-400 truncate">{m.email}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs font-black text-gray-700">${Number(m.amount || 0).toLocaleString()}</span>
                  <button onClick={() => removeMember(m.member_id)} className="text-gray-300 hover:text-rose-500 transition-colors" data-testid={`dr-syndicate-remove-${m.member_id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-[1fr_1fr_120px_auto] gap-2">
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Member name"
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#7B4FD8]" data-testid="dr-syndicate-name" />
        <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email (optional)"
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#7B4FD8]" data-testid="dr-syndicate-email" />
        <input value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value.replace(/[^0-9.]/g, '') }))} placeholder="Amount $"
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#7B4FD8]" data-testid="dr-syndicate-amount" />
        <button onClick={addMember} disabled={saving || !form.name.trim()}
          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#7B4FD8] text-white text-xs font-black hover:bg-[#5B32C8] transition-colors disabled:opacity-40"
          data-testid="dr-syndicate-add">
          <Plus size={13} /> Add
        </button>
      </div>
    </div>
  );
};

export default DataRoomSyndicate;
